import clsx from 'clsx';
import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscape } from '@/lib/hooks/useEscape';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { ns, useT } from '@/i18n';
import styles from './Popover.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name. */
  label: string;
  role?: 'menu' | 'dialog';
  children: ReactNode;
  className?: string;
}

/** Top-right panel (`.pop`): the account menu and the language/currency picker. A bottom sheet on mobile. */
export function Popover({ open, onClose, label, role = 'dialog', children, className }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const t = useT(ns.common);
  useFocusTrap(ref, open);
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <button type="button" className={styles.scrim} aria-label={t('close')} tabIndex={-1} onClick={onClose} />
      <div ref={ref} role={role} aria-label={label} tabIndex={-1} className={clsx(styles.pop, className)}>
        <span className={styles.grab} aria-hidden="true" />
        {children}
      </div>
    </>,
    document.body,
  );
}

export function PopoverGroup({ children }: { children: ReactNode }) {
  return <div className={styles.grp}>{children}</div>;
}
